import React, {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react'
import { useLocale } from 'next-intl'
import {
  FirstDataRenderedEvent,
  GetRowIdParams,
  ModuleRegistry,
  themeQuartz,
} from 'ag-grid-community'
import { AgGridReact, AgGridReactProps } from 'ag-grid-react'
import {
  AG_GRID_LOCALE_EN,
  AG_GRID_LOCALE_FR,
  AG_GRID_LOCALE_NL,
} from '@ag-grid-community/locale'
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise'
import { useSearchParams } from 'next/navigation'

import { selectNode } from '@/components/Grid/utils/grid.utils'
import { isRecord } from '@/utils/typeGuards'
import { buildQuickFilterParts, matchesQuickFilterParts } from './quickFilter'
ModuleRegistry.registerModules([AllEnterpriseModule])

if (process.env.NEXT_PUBLIC_AG_GRID_LICENSE_KEY) {
  LicenseManager.setLicenseKey(process.env.NEXT_PUBLIC_AG_GRID_LICENSE_KEY)
}

type AgGridReactInstanceWithContext = AgGridReact<unknown> & {
  context?: unknown
}

const resolveFallbackRowId = (params: GetRowIdParams): string => {
  const rowIndex = (params as { rowIndex?: unknown }).rowIndex
  if (typeof rowIndex === 'number') {
    return `missing-row-id:${rowIndex}`
  }

  const parentKeys = Array.isArray(params.parentKeys) ? params.parentKeys : []
  return `missing-row-id:${params.level}:${parentKeys.join('/')}`
}

const AGGridBase = forwardRef<AgGridReact, AgGridReactProps>((props, ref) => {
  const {
    cacheQuickFilter,
    onFirstDataRendered: onFirstDataRenderedProp,
    quickFilterMatcher: quickFilterMatcherProp,
    quickFilterParser: quickFilterParserProp,
    ...restProps
  } = props
  const locale = useLocale()
  const searchParams = useSearchParams()
  const gridRef = useRef<AgGridReact | null>(null)
  const normalizedQuickFilterCacheRef = useRef(new Map<string, string>())

  useImperativeHandle(ref, () => {
    // IMPORTANT:
    // Do NOT spread/copy `gridRef.current` into a new object.
    // AG Grid mutates the AgGridReact instance to attach `api` later.
    // If we copy too early, consumers can get a permanently-undefined `api`
    // until a full remount/refresh.
    const instance = gridRef.current
    if (instance) {
      ;(instance as AgGridReactInstanceWithContext).context = props.context
    }
    // Safe: child AgGridReact assigns gridRef.current before parent effects run.
    return instance!
  }, [props.context])

  const localeText = useMemo(() => {
    switch (locale) {
      case 'fr':
        return AG_GRID_LOCALE_FR
      case 'nl':
        return AG_GRID_LOCALE_NL
      case 'en':
        return AG_GRID_LOCALE_EN
      default:
        return AG_GRID_LOCALE_FR
    }
  }, [locale])

  const getRowId = useMemo(() => {
    return (params: GetRowIdParams) => {
      if (isRecord(params.data)) {
        const id = params.data.id
        if (typeof id === 'string' || typeof id === 'number') {
          return String(id)
        }
      }

      return resolveFallbackRowId(params)
    }
  }, [])

  const onFirstDataRendered = useCallback(
    (params: FirstDataRenderedEvent) => {
      const nodeId = searchParams?.get('node')
      if (nodeId) selectNode(params, nodeId)
      onFirstDataRenderedProp?.(params)
    },
    [onFirstDataRenderedProp, searchParams],
  )

  const popupParent = useMemo(() => {
    if (typeof window !== 'undefined') return document.querySelector('body')
  }, [])

  const quickFilterParser = useCallback((quickFilter: string) => {
    return buildQuickFilterParts(quickFilter)
  }, [])

  const quickFilterMatcher = useCallback(
    (quickFilterParts: string[], rowQuickFilterAggregateText: string) => {
      return matchesQuickFilterParts(
        quickFilterParts,
        rowQuickFilterAggregateText,
        normalizedQuickFilterCacheRef.current,
      )
    },
    [],
  )

  const gridTheme = useMemo(() => {
    return themeQuartz.withParams({
      accentColor: 'rgb(255, 200, 87)',
      rowHeight: 40,
    })
  }, [])

  return (
    <div className="ag-theme-quartz h-full w-full shadow-sm">
      <AgGridReact
        ref={gridRef}
        theme={gridTheme}
        localeText={localeText}
        getRowId={getRowId}
        popupParent={popupParent}
        excludeChildrenWhenTreeDataFiltering
        stopEditingWhenCellsLoseFocus
        suppressAggFuncInHeader
        groupAggFiltering
        animateRows={false}
        suppressColumnMoveAnimation
        accentedSort
        cacheQuickFilter={cacheQuickFilter ?? true}
        quickFilterParser={quickFilterParserProp ?? quickFilterParser}
        quickFilterMatcher={quickFilterMatcherProp ?? quickFilterMatcher}
        onFirstDataRendered={onFirstDataRendered}
        {...restProps}
      />
    </div>
  )
})

AGGridBase.displayName = 'AGGrid'

export const AGGrid = memo(AGGridBase)
AGGrid.displayName = 'AGGrid'
